package com.smashvoter.screens

import androidx.compose.runtime.*
import com.smashvoter.SmashColors
import com.smashvoter.api.ApiClient
import com.smashvoter.api.TrustRoundSummary
import com.smashvoter.api.TrustStandingEntry
import com.smashvoter.api.TrustSummaryResponse
import kotlinx.browser.document
import kotlinx.coroutines.launch
import org.jetbrains.compose.web.css.*
import org.jetbrains.compose.web.dom.*
import org.w3c.dom.HTMLStyleElement

private const val TRUST_STYLE_ID = "trust-screen-styles"

private val trustCss = """
    .trust-page {
        min-height: 100vh;
        background: ${SmashColors.background};
        color: ${SmashColors.textPrimary};
        font-family: 'Rubik', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
        padding: 0 0 3rem 0;
    }
    .trust-header {
        background: ${SmashColors.surface};
        border-bottom: 2px solid ${SmashColors.accent};
        padding: 1.5rem 2rem;
        display: flex;
        align-items: center;
        gap: 1rem;
    }
    .trust-header-title {
        font-size: 1.6rem;
        font-weight: 800;
        letter-spacing: -0.02em;
        color: ${SmashColors.accent};
        margin: 0;
    }
    .trust-header-sub {
        font-size: 0.85rem;
        color: ${SmashColors.textSecondary};
        margin: 0.2rem 0 0 0;
    }
    .trust-content {
        max-width: 1100px;
        margin: 0 auto;
        padding: 2rem 1.5rem;
        display: flex;
        flex-direction: column;
        gap: 2rem;
    }
    .round-card {
        background: ${SmashColors.surface};
        border: 1px solid ${SmashColors.border};
        border-radius: 12px;
        overflow: hidden;
    }
    .round-header {
        background: ${SmashColors.surfaceRaised};
        border-bottom: 1px solid ${SmashColors.border};
        padding: 0.9rem 1.25rem;
        display: flex;
        align-items: center;
        gap: 0.75rem;
    }
    .round-badge {
        background: ${SmashColors.accent};
        color: #fff;
        font-size: 0.75rem;
        font-weight: 800;
        text-transform: uppercase;
        letter-spacing: 0.08em;
        padding: 0.25rem 0.65rem;
        border-radius: 6px;
    }
    .round-title {
        font-size: 1rem;
        font-weight: 700;
        color: ${SmashColors.textPrimary};
        margin: 0;
    }
    .round-body {
        display: grid;
        grid-template-columns: 1fr 2fr;
        gap: 0;
    }
    .matches-section {
        padding: 1.25rem;
        border-right: 1px solid ${SmashColors.border};
    }
    .standings-section {
        padding: 1.25rem;
        display: flex;
        flex-direction: column;
        gap: 1rem;
    }
    .section-label {
        font-size: 0.72rem;
        font-weight: 700;
        text-transform: uppercase;
        letter-spacing: 0.1em;
        color: ${SmashColors.textSecondary};
        margin: 0 0 0.75rem 0;
    }
    .match-item {
        font-size: 0.88rem;
        color: ${SmashColors.textPrimary};
        padding: 0.5rem 0.75rem;
        border-radius: 6px;
        background: ${SmashColors.surfaceRaised};
        margin-bottom: 0.45rem;
        border-left: 3px solid ${SmashColors.border};
        line-height: 1.4;
        word-break: break-word;
    }
    .level-block {
        border-radius: 8px;
        overflow: hidden;
        border: 1px solid ${SmashColors.border};
    }
    .level-header {
        padding: 0.45rem 0.85rem;
        font-size: 0.72rem;
        font-weight: 800;
        text-transform: uppercase;
        letter-spacing: 0.1em;
    }
    .level-header.advanced   { background: rgba(251,191,36,0.12); color: #fbbf24; }
    .level-header.intermediate { background: rgba(96,165,250,0.12); color: #60a5fa; }
    .level-header.beginner   { background: rgba(74,222,128,0.12); color: #4ade80; }
    .standings-table {
        width: 100%;
        border-collapse: collapse;
        font-size: 0.84rem;
    }
    .standings-table th {
        text-align: left;
        padding: 0.35rem 0.7rem;
        font-size: 0.7rem;
        font-weight: 600;
        text-transform: uppercase;
        letter-spacing: 0.07em;
        color: ${SmashColors.textSecondary};
        border-bottom: 1px solid ${SmashColors.border};
        background: ${SmashColors.surfaceRaised};
    }
    .standings-table td {
        padding: 0.45rem 0.7rem;
        border-bottom: 1px solid rgba(60,60,62,0.5);
        color: ${SmashColors.textSecondary};
    }
    .standings-table tr:last-child td { border-bottom: none; }
    .standings-table .td-rank { width: 2rem; font-weight: 700; color: ${SmashColors.textPrimary}; }
    .standings-table .td-name { font-weight: 600; color: ${SmashColors.textPrimary}; }
    .standings-table .td-num  { text-align: right; font-variant-numeric: tabular-nums; }
    .standings-table tr:first-child td.td-name { color: ${SmashColors.accent}; }
    .empty-level { padding: 0.6rem 0.85rem; font-size: 0.82rem; color: ${SmashColors.textSecondary}; font-style: italic; }
    .trust-empty {
        text-align: center;
        padding: 5rem 1rem;
        color: ${SmashColors.textSecondary};
    }
    .trust-empty-icon { font-size: 3rem; margin-bottom: 1rem; }
    .trust-empty-msg  { font-size: 1.1rem; }
    .trust-loading {
        text-align: center;
        padding: 5rem 1rem;
        color: ${SmashColors.textSecondary};
        font-size: 1rem;
    }
    .trust-error {
        text-align: center;
        padding: 3rem 1rem;
        color: #e94f37;
        font-size: 0.95rem;
    }
    @media (max-width: 760px) {
        .round-body { grid-template-columns: 1fr; }
        .matches-section { border-right: none; border-bottom: 1px solid ${SmashColors.border}; }
        .trust-header { padding: 1.2rem 1rem; }
        .trust-content { padding: 1.25rem 1rem; }
    }
""".trimIndent()

@Composable
fun TrustScreen(apiClient: ApiClient) {
    // Inject CSS
    DisposableEffect(Unit) {
        if (document.getElementById(TRUST_STYLE_ID) == null) {
            val styleEl = document.createElement("style") as HTMLStyleElement
            styleEl.id = TRUST_STYLE_ID
            styleEl.textContent = trustCss
            document.head?.appendChild(styleEl)
        }
        onDispose { document.getElementById(TRUST_STYLE_ID)?.remove() }
    }

    val scope = rememberCoroutineScope()
    var data by remember { mutableStateOf<TrustSummaryResponse?>(null) }
    var loading by remember { mutableStateOf(true) }
    var error by remember { mutableStateOf<String?>(null) }

    LaunchedEffect(Unit) {
        scope.launch {
            try {
                data = apiClient.getTrustData()
            } catch (e: Exception) {
                error = e.message ?: "Failed to load tournament results"
            } finally {
                loading = false
            }
        }
    }

    Div(attrs = { classes("trust-page") }) {
        // ── Header ──────────────────────────────────────────────────────────
        Div(attrs = { classes("trust-header") }) {
            Div {
                H1(attrs = { classes("trust-header-title") }) { Text("🏆 Tournament Results") }
                P(attrs = { classes("trust-header-sub") }) { Text("Live standings after every round") }
            }
        }

        // ── Content ─────────────────────────────────────────────────────────
        Div(attrs = { classes("trust-content") }) {
            when {
                loading -> Div(attrs = { classes("trust-loading") }) { Text("Loading results…") }
                error != null -> Div(attrs = { classes("trust-error") }) { Text("⚠️ $error") }
                data == null || data!!.rounds.isEmpty() -> {
                    Div(attrs = { classes("trust-empty") }) {
                        Div(attrs = { classes("trust-empty-icon") }) { Text("🎮") }
                        Div(attrs = { classes("trust-empty-msg") }) { Text("No rounds have been played yet. Check back soon!") }
                    }
                }
                else -> {
                    data!!.rounds.forEach { round -> RoundCard(round) }
                }
            }
        }
    }
}

@Composable
private fun RoundCard(round: TrustRoundSummary) {
    Div(attrs = { classes("round-card") }) {
        // Round header
        Div(attrs = { classes("round-header") }) {
            Span(attrs = { classes("round-badge") }) { Text("Round") }
            H2(attrs = { classes("round-title") }) { Text("${round.roundIndex}") }
        }

        // Body: matches + standings side by side
        Div(attrs = { classes("round-body") }) {
            // Matches
            Div(attrs = { classes("matches-section") }) {
                P(attrs = { classes("section-label") }) { Text("Matches") }
                if (round.matches.isEmpty()) {
                    Div(attrs = { style { color(Color(SmashColors.textSecondary)); fontSize(0.85.cssRem) } }) {
                        Text("No matches recorded.")
                    }
                } else {
                    round.matches.forEach { match ->
                        Div(attrs = { classes("match-item") }) { Text(match) }
                    }
                }
            }

            // Standings per level
            Div(attrs = { classes("standings-section") }) {
                P(attrs = { classes("section-label") }) { Text("Standings after this round") }

                LevelBlock("Advanced", "advanced", round.standingsByLevel["ADVANCED"] ?: emptyList())
                LevelBlock("Intermediate", "intermediate", round.standingsByLevel["INTERMEDIATE"] ?: emptyList())
                LevelBlock("Beginner", "beginner", round.standingsByLevel["BEGINNER"] ?: emptyList())
            }
        }
    }
}

@Composable
private fun LevelBlock(label: String, cssClass: String, entries: List<TrustStandingEntry>) {
    Div(attrs = { classes("level-block") }) {
        Div(attrs = { classes("level-header", cssClass) }) { Text(label) }
        if (entries.isEmpty()) {
            Div(attrs = { classes("empty-level") }) { Text("No players in this level.") }
        } else {
            Table(attrs = { classes("standings-table") }) {
                Thead {
                    Tr {
                        Th { Text("#") }
                        Th { Text("Player") }
                        Th(attrs = { style { textAlign("right") } }) { Text("Wins") }
                        Th(attrs = { style { textAlign("right") } }) { Text("Stocks") }
                        Th(attrs = { style { textAlign("right") } }) { Text("ELO") }
                    }
                }
                Tbody {
                    entries.forEachIndexed { index, entry ->
                        val medal = when (index) { 0 -> "🥇"; 1 -> "🥈"; 2 -> "🥉"; else -> "${index + 1}" }
                        Tr {
                            Td(attrs = { classes("td-rank") }) { Text(medal) }
                            Td(attrs = { classes("td-name") }) { Text(entry.playerName) }
                            Td(attrs = { classes("td-num") }) { Text("${entry.wins}") }
                            Td(attrs = { classes("td-num") }) { Text("${entry.stocks}") }
                            Td(attrs = { classes("td-num") }) { Text(entry.elo.toInt().toString()) }
                        }
                    }
                }
            }
        }
    }
}
